import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import BrandMark from '../components/BrandMark';
import { useBranding } from '../hooks/useBranding.jsx';
import { forgotPassword, resetPassword } from '../services/emailApi';

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';
  const isAddAccount = searchParams.get('add') === '1' || searchParams.get('switch') === '1';
  const [step, setStep] = useState('login'); // login, forgot, reset
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [hostSso, setHostSso] = useState(false);
  // On a custom domain (mail.datosmcp.com) password login stays the visible UI; SSO is a
  // discreet corner link (owners/members reach app mailboxes through it).
  const isCustomDomain = (() => {
    const h = window.location.hostname;
    return h !== 'localhost' && h !== '127.0.0.1' && h !== 'jubly.net' && !h.endsWith('.jubly.net');
  })();
  const ssoError = searchParams.get('sso_error') === '1';
  // App host (branding resolved for this custom domain): the page is the app's mailbox
  // login — its own name and logo, no sign-up, and a plain "Powered by Jubly" footer.
  const isApp = branding.isApp;
  const appTitle = branding.name ? `${branding.name} Mail` : 'Mail';

  useEffect(() => {
    if (!isApp || branding.pending) return;
    const prev = document.title;
    document.title = appTitle;
    return () => { document.title = prev; };
  }, [isApp, branding.pending, appTitle]);

  const defaultRoute = (() => {
    const h = window.location.hostname;
    if (h.startsWith('calendar.') || h.startsWith('cal.')) return '/calendar';
    if (h.startsWith('voice.') || h.startsWith('phone.')) return '/voice';
    if (h.startsWith('admin.')) return '/admin';
    return '/email';
  })();

  // Only show "Sign in with EZLogin" (the imagia idp now authenticates at auth.ezlogin.us) when the backend reports OIDC is configured.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/oidc/status', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { enabled: false }))
      .then(d => { if (!cancelled) { setOidcEnabled(!!d.enabled); setHostSso(!!d.host_sso); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const ssoHref = `/api/auth/oidc/login?idp=imagia&return_to=${encodeURIComponent(searchParams.get('return_to') || defaultRoute)}`;
  const signInWithImagia = () => { window.location.href = ssoHref; };

  if (isAuthenticated && !isAddAccount) {
    navigate(defaultRoute, { replace: true });
    return null;
  }

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate(defaultRoute);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      setMessage('Check your recovery email for a reset code.');
      setStep('reset');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword({ email: email.trim(), code: code.trim(), newPassword });
      setMessage('Password reset! You can now sign in.');
      setStep('login');
      setPassword('');
      setCode('');
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <nav className="flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark size={24} />
          <span className="font-bold text-gray-900 text-sm">{isApp ? appTitle : branding.name}</span>
        </Link>
        {!isApp && <Link to="/signup" className="text-sm text-blue-600 hover:text-blue-800 font-medium">Create account</Link>}
      </nav>
      <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 64px)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <BrandMark size={64} rounded="rounded-2xl" className="mx-auto shadow-lg" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {step === 'login' ? (isApp ? appTitle : `Sign in to ${branding.name}`) : step === 'forgot' ? 'Reset password' : 'Enter reset code'}
          </h1>
          {isApp && step === 'login' && branding.name && (
            <p className="mt-1 text-sm text-gray-600">Sign in to your {branding.name} mailbox</p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          {message && (
            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700 mb-4">{message}</div>
          )}

          {step === 'login' && ssoError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4">Single sign-on didn't complete. Please try again.</div>
          )}

          {step === 'login' && oidcEnabled && !isCustomDomain && (
            <div className="mb-5">
              <button
                type="button"
                onClick={signInWithImagia}
                className="w-full rounded-lg px-4 py-3 text-sm font-semibold text-white transition-colors shadow-sm"
                style={{ background: 'linear-gradient(90deg, #4f46e5, #7c3aed)' }}
              >
                Sign in with EZLogin
              </button>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="px-2 bg-white text-gray-400">or sign in with email</span></div>
              </div>
            </div>
          )}

          {step === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input type="email" required autoFocus autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
              </div>
              {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
              <button type="submit" disabled={loading || !email.trim()}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm">
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => { setStep('forgot'); setError(''); setMessage(''); }} className="text-blue-600 hover:text-blue-800">
                  Forgot password?
                </button>
              </div>
            </form>
          )}

          {step === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-4">
              <p className="text-sm text-gray-600">Enter your email and we'll send a reset code to your recovery email.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
              </div>
              {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
              <button type="submit" disabled={loading}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {loading ? 'Sending...' : 'Send reset code'}
              </button>
              <button type="button" onClick={() => { setStep('login'); setError(''); setMessage(''); }} className="w-full text-xs text-gray-500 hover:text-gray-700">
                Back to sign in
              </button>
            </form>
          )}

          {step === 'reset' && (
            <form onSubmit={handleReset} className="space-y-4">
              <p className="text-sm text-gray-600">Enter the 6-digit code from your recovery email and your new password.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Reset code</label>
                <input type="text" required autoFocus maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-center text-xl font-mono tracking-[0.4em] focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New password</label>
                <input type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters" minLength={8} className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
              </div>
              {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
              <button type="submit" disabled={loading || code.length !== 6 || newPassword.length < 8}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {loading ? 'Resetting...' : 'Reset password'}
              </button>
              <button type="button" onClick={() => { setStep('login'); setError(''); setMessage(''); }} className="w-full text-xs text-gray-500 hover:text-gray-700">
                Back to sign in
              </button>
            </form>
          )}

          {!isApp && (
            <p className="mt-6 text-center text-xs text-gray-500">
              New here?{' '}
              <Link to="/signup" className="text-blue-600 hover:text-blue-800 font-medium">Create an account</Link>
            </p>
          )}
        </div>
        {isApp && (
          <footer className="mt-6 text-center text-xs text-gray-500 space-y-1">
            <p>
              Mail for {branding.name || 'this domain'} · Powered by{' '}
              <a href="https://jubly.net" className="font-medium text-gray-600 hover:text-gray-800 underline-offset-2 hover:underline">Jubly</a>
            </p>
            <p className="space-x-3">
              <a href="https://jubly.net/privacy" className="hover:text-gray-700 hover:underline">Privacy</a>
              <a href="https://jubly.net/terms" className="hover:text-gray-700 hover:underline">Terms</a>
            </p>
          </footer>
        )}
      </div>
      </div>
      {oidcEnabled && hostSso && isCustomDomain && step === 'login' && (
        <a href={ssoHref}
          className="fixed bottom-3 right-4 text-[11px] text-gray-400/70 hover:text-gray-500 hover:underline">
          Sign in with EZLogin
        </a>
      )}
    </div>
  );
}
